import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CurrencyTagsInput from '../../../components/form/currency-tags-input.jsx';
import { PROFILE_SORTABLE_COLUMNS, PROFILE_STATUS_OPTIONS } from '../../../config/enum-options.js';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';
import {
  FilterPresetList,
  FilterPresetNameModal,
  FilterPresetSaveButton,
} from '../../../shared/filter-presets/filter-presets.jsx';
import AssignmentModal from '../../../shared/ui/assignment-modal/assignment-modal.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';

function ProfilesPageView({
  rows,
  countryOptions,
  filters,
  isFilterOpen,
  isLoading,
  isLoadingMore,
  hasMoreProfiles,
  error,
  isAssignModalOpen,
  assignTargetProfileName,
  adminProfileOptions,
  isAdminProfilesLoading,
  selectedManagerId,
  isAssignmentSaving,
  assignmentError,
  toggleSort,
  updateFilter,
  resetAndCloseFilters,
  setIsFilterOpen,
  openAssignModal,
  closeAssignModal,
  onSelectManager,
  onCountriesFilterChange,
  assignManager,
  unassignManager,
  sortIndicator,
  loadMoreProfiles,
  filterPresets,
  isFilterPresetModalOpen,
  filterPresetName,
  filterPresetNameError,
  openSaveFilterPreset,
  closeSaveFilterPreset,
  setFilterPresetName,
  saveFilterPreset,
  applyFilterPreset,
  deleteFilterPreset,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusBadgeClass = (statusValue) => {
    const status = String(statusValue || '').trim().toLowerCase();
    const map = {
      verified: 'bg-success-subtle text-success',
      pending: 'bg-warning-subtle text-warning',
      verification: 'bg-warning-subtle text-warning',
      new: 'bg-info-subtle text-info',
      completed: 'bg-primary-subtle text-primary',
      limited: 'bg-secondary-subtle text-secondary',
      disabled: 'bg-danger-subtle text-danger',
    };

    return map[status] || 'bg-secondary-subtle text-secondary';
  };

  const openProfile = (id) => {
    if (id) {
      navigate(`/profiles/${encodeURIComponent(id)}`);
    }
  };

  return (
    <div>
      <FloatingAlerts error={error} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('profiles.title')}</h2>
          <p>{t('profiles.subtitle')}</p>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
          <div className="form-check form-switch mb-0">
            <input
              id="profilesAssignedOnly"
              type="checkbox"
              name="assignedOnly"
              className="form-check-input"
              checked={filters.assignedOnly}
              onChange={updateFilter}
            />
            <label htmlFor="profilesAssignedOnly" className="form-check-label">
              {t('profiles.mineOnly')}
            </label>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={() => setIsFilterOpen((prev) => !prev)}
          >
            <i className="ti ti-adjustments-horizontal me-1 fs-5"></i>
            {t('profiles.filters')}
          </button>
        </div>
      </div>

      <div className="widget-content searchable-container list">
        {isFilterOpen ? (
          <div className="card card-body">
            <div className="row g-2">
              <div className="col-xl-3 col-md-6">
                <input
                  type="text"
                  name="firstName"
                  className="form-control"
                  placeholder={t('profiles.firstName')}
                  value={filters.firstName}
                  onChange={updateFilter}
                />
              </div>
              <div className="col-xl-3 col-md-6">
                <input
                  type="text"
                  name="lastName"
                  className="form-control"
                  placeholder={t('profiles.lastName')}
                  value={filters.lastName}
                  onChange={updateFilter}
                />
              </div>
              <div className="col-xl-3 col-md-6">
                <input
                  type="text"
                  name="email"
                  className="form-control"
                  placeholder={t('common.email')}
                  value={filters.email}
                  onChange={updateFilter}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <select
                  name="status"
                  className="form-control"
                  value={filters.status}
                  onChange={updateFilter}
                >
                  <option value="">{t('profiles.anyStatus')}</option>
                  {PROFILE_STATUS_OPTIONS.map((statusValue) => (
                    <option key={statusValue} value={statusValue}>
                      {getEnumOptionLabel(t, 'profileStatus', statusValue)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-xl-1 col-md-2">
                <div className="d-flex gap-2">
                  <FilterPresetSaveButton onClick={openSaveFilterPreset} className="flex-fill" />
                  <button type="button" className="btn btn-outline-secondary flex-fill" onClick={resetAndCloseFilters}>
                    <i className="ti ti-x"></i>
                  </button>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label mb-1">{t('profiles.countries')}</label>
                <CurrencyTagsInput
                  value={filters.countriesOnly}
                  options={countryOptions}
                  onChange={onCountriesFilterChange}
                  placeholderText="Select one or more countries"
                />
              </div>
              <div className="col-12">
                <div className="form-check form-switch mb-0">
                  <input
                    id="profilesShowInvalidated"
                    type="checkbox"
                    name="showInvalidated"
                    className="form-check-input"
                    checked={filters.showInvalidated}
                    onChange={updateFilter}
                  />
                  <label htmlFor="profilesShowInvalidated" className="form-check-label">
                    {t('profiles.showInvalidated')}
                  </label>
                </div>
              </div>
            </div>
            <FilterPresetList presets={filterPresets} onApply={applyFilterPreset} onDelete={deleteFilterPreset} />
          </div>
        ) : null}

        <div className={`card card-body${isFilterOpen ? ' mt-3' : ''}`}>
          <div className="table-responsive">
            <table className="table search-table align-middle text-nowrap mb-0">
              <thead className="header-item">
                <tr>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(PROFILE_SORTABLE_COLUMNS.name)}>
                      {t('common.name')} <i className={`${sortIndicator(PROFILE_SORTABLE_COLUMNS.name)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(PROFILE_SORTABLE_COLUMNS.email)}>
                      {t('common.email')} <i className={`${sortIndicator(PROFILE_SORTABLE_COLUMNS.email)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(PROFILE_SORTABLE_COLUMNS.country)}>
                      {t('profiles.country')} <i className={`${sortIndicator(PROFILE_SORTABLE_COLUMNS.country)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>{t('common.status')}</th>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(PROFILE_SORTABLE_COLUMNS.created)}>
                      {t('profiles.created')} <i className={`${sortIndicator(PROFILE_SORTABLE_COLUMNS.created)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>{t('profiles.assignedTo')}</th>
                </tr>
              </thead>

              <tbody>
                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">{t('profiles.noProfiles')}</td>
                  </tr>
                ) : null}

                {rows.map((row) => (
                  <tr
                    key={row.key}
                    className={row.id ? 'search-items app-clickable-row' : 'search-items'}
                    onClick={() => openProfile(row.id)}
                  >
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="user-meta-info">
                          <h6 className="user-name mb-0">{row.name}</h6>
                        </div>
                      </div>
                    </td>
                    <td>{row.email}</td>
                    <td>{row.country}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className={`badge rounded-pill fw-semibold text-uppercase ${getStatusBadgeClass(row.status)}`}>
                          {row.status || '-'}
                        </span>
                        {row.invalid ? (
                          <span className="badge rounded-pill fw-semibold text-uppercase bg-danger-subtle text-danger">
                            {t('profiles.invalidated')}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td>{row.createdAt}</td>
                    <td>
                      <button
                        type="button"
                        className={`btn btn-sm ${row.assignedToName && row.assignedToName !== '-' ? 'btn-outline-primary' : 'btn-primary'}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          openAssignModal(row);
                        }}
                        disabled={!row.id}
                      >
                        {row.assignedToName && row.assignedToName !== '-' ? row.assignedToName : t('common.assign')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMoreProfiles && !isLoading ? (
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={loadMoreProfiles}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('common.loading') : t('profiles.loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <AssignmentModal
        isOpen={isAssignModalOpen}
        title={t('profiles.assignProfileManager')}
        targetLabel={t('profiles.profile')}
        targetName={assignTargetProfileName}
        error={assignmentError}
        options={adminProfileOptions}
        isOptionsLoading={isAdminProfilesLoading}
        selectedId={selectedManagerId}
        isSaving={isAssignmentSaving}
        showUnassign
        onClose={closeAssignModal}
        onSelect={onSelectManager}
        onAssign={assignManager}
        onUnassign={unassignManager}
      />
      <FilterPresetNameModal
        isOpen={isFilterPresetModalOpen}
        name={filterPresetName}
        error={filterPresetNameError}
        onNameChange={setFilterPresetName}
        onSubmit={saveFilterPreset}
        onClose={closeSaveFilterPreset}
      />
    </div>
  );
}

export default ProfilesPageView;
